import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, PieChart, Pie, Cell, BarChart, Bar,
} from 'recharts';
import { formatRelativeTime, formatDueDate } from '../utils/relativeTime';

// Bu interfeys backend /dashboard/stats javobini aks ettiradi (qarang
// backend/app/routers/dashboard.py). Har bir maydon IXTIYORIY - backend
// har bir foydalanuvchiga FAQAT o'ziga (roli+ko'lami bo'yicha) tegishli
// maydonlarni qaytaradi, xavfsizlik chegarasi shu yerda emas, backendda
// (app/core/access_scope.py). Frontend faqat kelgan narsani ko'rsatadi.
interface Stats {
  scope: { level: 'company' | 'department' | 'self'; department: string | null; role: string };
  products?: { total: number; low_stock: number };
  inventory_summary?: { in_stock: number; low_stock: number; out_of_stock: number; on_order: number };
  clients?: number;
  suppliers?: number;
  quotations?: { total: number; accepted: number; pending: number; sales?: number; service?: number };
  purchases?: { total: number; pending: number };
  cases?: {
    total: number; open: number; urgent: number;
    sales?: number; service?: number;
    by_status?: Record<string, number>;
  };
  finance?: {
    income_usd: number; income_uzs: number;
    expense_usd: number; profit_usd: number;
    unpaid_invoices: number; overdue_invoices: number;
    active_contracts: number; expiring_contracts: number;
  };
  contracts?: { active: number; expiring: number };
  warranty_expiring?: number;
}

// /dashboard/monthly-overview va /dashboard/top-products javoblari - Stage A
// "arzon" widgetlar (backend/app/routers/dashboard.py). Ikkalasi ham
// resolve_scope()/scope_filter() orqali - rol uchun ma'noli bo'lmasa
// (masalan ombor/buxgalteriya) backend BO'SH ro'yxat qaytaradi, frontend
// bunda widgetni shunchaki ko'rsatmaydi.
interface MonthlyOverview {
  months: { month: string; count: number; revenue: Record<string, number> }[];
}
interface TopProduct { name: string; quantity: number; revenue: Record<string, number> }
interface TopProducts { products: TopProduct[] }

// /dashboard/recent-activity javobi - "So'nggi faoliyat" widgeti (Option A,
// STAGE hisobotiga qarang: alohida audit_log jadvalisiz, mavjud
// created_at/closed_at'lardan hosil qilingan). Backend HAR BIR entity turi
// uchun scope_filter() qo'llaydi - frontend faqat kelganini ko'rsatadi,
// link=null bo'lsa (masalan warehouse_head uchun shartnoma) sahifaga
// o'tuvchi havola CHIZILMAYDI (403'ga olib bormaslik uchun).
interface ActivityItem {
  key: string;
  entity_type: 'quotation' | 'case' | 'contract' | 'purchase' | 'payment';
  subtype: string | null;
  label: string;
  timestamp: string;
  link: string | null;
}

// /tasks javobi (owner-scoped - qarang backend/app/routers/tasks.py) -
// "Tasks & Reminders" widgeti. Model P (shaxsiy) + Model A (tayinlangan)
// bitta jadvalda: `view=mine` FAQAT joriy user egasi bo'lgan vazifalarni
// qaytaradi (shaxsiy + qabul qilingan/bajarilgan tayinlangan) - boshqa
// hech kimning vazifasi bu yerga sizib kelmaydi, hatto boshliq/direktor
// uchun ham (backend _visibility_filter + view filtri).
interface TaskItem {
  id: number;
  title: string;
  due_date: string | null;
  priority: 'low' | 'medium' | 'high';
  done: boolean;
  status: 'personal' | 'pending' | 'accepted' | 'rejected' | 'done';
  assigned_by_name?: string | null;
}

const monthNames: Record<string, string> = {
  '01': 'Yan', '02': 'Fev', '03': 'Mar', '04': 'Apr',
  '05': 'May', '06': 'Iyn', '07': 'Iyl', '08': 'Avg',
  '09': 'Sen', '10': 'Okt', '11': 'Noy', '12': 'Dek',
};

const currencySymbol: Record<string, string> = { usd: '$', eur: '€', uzs: "so'm" };
const formatCurrency = (amount: number, currency: string) =>
  `${amount.toLocaleString()} ${currencySymbol[currency] || currency.toUpperCase()}`;

const caseStatusLabels: Record<string, { label: string; color: string }> = {
  new:         { label: 'Yangi',       color: '#3B82F6' },
  in_progress: { label: 'Jarayonda',   color: '#F59E0B' },
  waiting:     { label: 'Kutmoqda',    color: '#F97316' },
  resolved:    { label: 'Hal qilindi', color: '#10B981' },
  closed:      { label: 'Yopildi',     color: '#6B7280' },
  cancelled:   { label: 'Bajarilmadi', color: '#EF4444' },
};

const departmentLabels: Record<string, string> = {
  sales: "Sotuv bo'limi",
  service: 'Servis bo\'limi',
  warehouse: 'Ombor/logistika bo\'limi',
  accounting: 'Buxgalteriya',
};

// ===== Stage A widgetlari endi recharts orqali (o'rnatildi - package.json).
// Skalalash mantiqi (nechta oy, foiz, ustuvorlik tartibi) O'ZGARMAGAN - faqat
// chizish texnologiyasi SVG/CSS'dan recharts'ga ko'chirildi. =====

type MonthlyChartPoint = { month: string; count: number; revenue: Record<string, number> };

// Grafik ustidagi tooltip - sanani, aniq son (count) va valyuta bo'yicha
// ALOHIDA daromadni ko'rsatadi (hech qachon bitta summaga qo'shilmaydi).
const MonthlyTrendTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload as MonthlyChartPoint;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-gray-700 mb-1">{p.month}</p>
      <p className="text-blue-600">{p.count.toLocaleString()} ta taklif</p>
      {Object.entries(p.revenue).map(([cur, amt]) => (
        <p key={cur} className="text-gray-500">{formatCurrency(Math.round(amt), cur)}</p>
      ))}
    </div>
  );
};

// Oylik trend - recharts AreaChart (silliq monotone chiziq, hover tooltip,
// animatsiya). ResponsiveContainer konteyner balandligi Tailwind orqali
// (h-28 md:h-36 - Stage A'dagi bilan bir xil) beriladi, shuning uchun
// telefon ekranida ham to'lib-toshmaydi.
const MonthlyTrendChart: React.FC<{ months: MonthlyOverview['months'] }> = ({ months }) => {
  const chartData: MonthlyChartPoint[] = months.map(m => ({
    month: monthNames[m.month.split('-')[1]] || m.month,
    count: m.count,
    revenue: m.revenue,
  }));

  const totalRevenue: Record<string, number> = {};
  months.forEach(m => Object.entries(m.revenue).forEach(([cur, amt]) => {
    totalRevenue[cur] = (totalRevenue[cur] || 0) + amt;
  }));

  return (
    <div>
      <div className="w-full h-28 md:h-36">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 8, right: 4, left: -28, bottom: 0 }}>
            <defs>
              <linearGradient id="monthlyTrendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.28} />
                <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F4F6" />
            <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
            <YAxis hide domain={[0, (dataMax: number) => Math.max(dataMax, 1)]} />
            <Tooltip content={<MonthlyTrendTooltip />} cursor={{ stroke: '#3B82F6', strokeOpacity: 0.15, strokeWidth: 24 }} />
            <Area
              type="monotone" dataKey="count"
              stroke="#3B82F6" strokeWidth={2.5} fill="url(#monthlyTrendFill)"
              dot={{ r: 3.5, fill: '#3B82F6', strokeWidth: 0 }}
              activeDot={{ r: 5, fill: '#3B82F6', stroke: '#fff', strokeWidth: 2 }}
              isAnimationActive animationDuration={700} animationEasing="ease-out"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {Object.keys(totalRevenue).length > 0 && (
        <div className="flex gap-2 mt-3 flex-wrap">
          {Object.entries(totalRevenue).map(([cur, amt]) => (
            <span key={cur} className="text-[11px] bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
              {formatCurrency(Math.round(amt), cur)} qabul qilindi
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// Ombor holati donut - CSS conic-gradient (4 ta O'ZARO ISTISNO toifa,
// backend/app/routers/dashboard.py _inventory_summary bilan bir xil
// ustunlik tartibi: Buyurtma qilingan > Tugagan > Kam qoldiq > Yetarli).
const INVENTORY_SEGMENTS: { key: keyof NonNullable<Stats['inventory_summary']>; label: string; color: string }[] = [
  { key: 'in_stock', label: 'Yetarli', color: '#10B981' },
  { key: 'low_stock', label: 'Kam qoldiq', color: '#F59E0B' },
  { key: 'out_of_stock', label: 'Tugagan', color: '#EF4444' },
  { key: 'on_order', label: 'Buyurtma qilingan', color: '#3B82F6' },
];

const InventoryDonutTooltip: React.FC<any> = ({ active, payload, total }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0];
  const pct = total > 0 ? ((p.value / total) * 100).toFixed(1) : '0';
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold" style={{ color: p.payload.fill }}>{p.name}</p>
      <p className="text-gray-600">{Number(p.value).toLocaleString()} dona ({pct}%)</p>
    </div>
  );
};

const InventoryDonut: React.FC<{ data: NonNullable<Stats['inventory_summary']> }> = ({ data }) => {
  const total = INVENTORY_SEGMENTS.reduce((s, seg) => s + data[seg.key], 0);
  if (total === 0) return <p className="text-gray-400 text-sm text-center py-6">Mahsulot yo'q</p>;

  const chartData = INVENTORY_SEGMENTS.map(seg => ({ name: seg.label, value: data[seg.key], fill: seg.color }));

  return (
    <div className="flex flex-col sm:flex-row items-center gap-4 md:gap-6">
      <div className="relative w-28 h-28 md:w-32 md:h-32 flex-shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData} dataKey="value" nameKey="name"
              cx="50%" cy="50%" innerRadius="62%" outerRadius="100%"
              paddingAngle={chartData.filter(d => d.value > 0).length > 1 ? 2 : 0}
              stroke="none" isAnimationActive animationDuration={700} animationBegin={0}
            >
              {chartData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
            </Pie>
            <Tooltip content={<InventoryDonutTooltip total={total} />} />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-lg md:text-xl font-bold text-gray-800">{total}</span>
          <span className="text-[9px] text-gray-400">jami</span>
        </div>
      </div>
      <div className="flex-1 w-full space-y-1.5">
        {INVENTORY_SEGMENTS.map(seg => (
          <div key={seg.key} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: seg.color }} />
              <span className="text-gray-600">{seg.label}</span>
            </div>
            <span className="font-semibold text-gray-800">{data[seg.key]}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Eng ko'p sotilgan mahsulotlar/xizmatlar - miqdor (quantity) bo'yicha
// saralangan gorizontal ustunli grafik (recharts BarChart). Bar UZUNLIGI
// faqat quantity'ga bog'liq - valyutalar hech qachon bitta songa
// qo'shilmaydi, alohida-alohida tooltip/pastki qatorda ko'rsatiladi.
type TopProductRow = TopProduct & { rank: number; label: string };

const TOP_PRODUCT_LABEL_MAX = 22;
const truncateLabel = (s: string) => (s.length > TOP_PRODUCT_LABEL_MAX ? s.slice(0, TOP_PRODUCT_LABEL_MAX - 1) + '…' : s);

const TopProductsTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload as TopProductRow;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs max-w-[220px]">
      <p className="font-semibold text-gray-700 mb-1">{p.name}</p>
      <p className="text-blue-600">{p.quantity.toLocaleString()} dona</p>
      {Object.entries(p.revenue).map(([cur, amt]) => (
        <p key={cur} className="text-gray-500">{formatCurrency(amt, cur)}</p>
      ))}
    </div>
  );
};

const TopProductsList: React.FC<{ products: TopProduct[] }> = ({ products }) => {
  if (products.length === 0) return <p className="text-gray-400 text-sm text-center py-6">Hali qabul qilingan taklif yo'q</p>;

  const chartData: TopProductRow[] = products.map((p, i) => ({
    ...p, rank: i + 1, label: `${i + 1}. ${truncateLabel(p.name)}`,
  }));
  const chartHeight = Math.max(chartData.length * 36, 100);

  return (
    <div style={{ width: '100%', height: chartHeight }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 12, left: 0, bottom: 0 }}>
          <XAxis type="number" hide />
          <YAxis
            type="category" dataKey="label" width={128}
            tick={{ fontSize: 11, fill: '#374151' }} axisLine={false} tickLine={false}
          />
          <Tooltip content={<TopProductsTooltip />} cursor={{ fill: '#F3F4F6' }} />
          <Bar dataKey="quantity" fill="#3B82F6" radius={[0, 6, 6, 0]} barSize={14} isAnimationActive animationDuration={700} animationEasing="ease-out" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// entity_type + subtype -> ikonka (App.tsx nav ikonkalari bilan bir xil
// konventsiya: sotuv 📈/💼, servis 🛠️/🔧).
const ACTIVITY_ICON: Record<string, string> = {
  'quotation-sales': '📈', 'quotation-service': '🛠️',
  'case-sales': '💼', 'case-service': '🔧',
  'contract-x': '📄', 'purchase-x': '📋', 'payment-x': '💳',
};
const getActivityIcon = (item: ActivityItem) => ACTIVITY_ICON[`${item.entity_type}-${item.subtype || 'x'}`] || '📌';

const RecentActivity: React.FC<{ items: ActivityItem[] }> = ({ items }) => {
  if (items.length === 0) {
    return <p className="text-gray-400 text-sm text-center py-6">Hozircha faoliyat yo'q</p>;
  }
  return (
    <div className="divide-y divide-gray-100">
      {items.map(item => {
        const body = (
          <div className="flex items-start gap-3 py-2.5">
            <span className="text-lg flex-shrink-0 leading-none mt-0.5">{getActivityIcon(item)}</span>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-gray-700 leading-snug break-words">{item.label}</p>
              <p className="text-xs text-gray-400 mt-0.5">{formatRelativeTime(item.timestamp, 'uz')}</p>
            </div>
          </div>
        );
        return item.link ? (
          <Link key={item.key} to={item.link} className="block -mx-2 px-2 rounded-lg hover:bg-gray-50 transition">
            {body}
          </Link>
        ) : (
          <div key={item.key} className="-mx-2 px-2">{body}</div>
        );
      })}
    </div>
  );
};

const PRIORITY_DOT: Record<string, string> = { low: '#9CA3AF', medium: '#3B82F6', high: '#EF4444' };

// Dashboard vidjeti - faqat bajarilmagan vazifalarni ko'rsatadi (bajarilgan
// belgilangach ro'yxatdan chiqib ketadi - "bugungi ish ro'yxati" sifatida
// ishlatilishi uchun eng mos, doim TO'LIQ ro'yxatni bulg'amaydi. To'liq
// tarix - shu jumladan bajarilganlar - /tasks sahifasida ko'rinadi).
// Bitta "Menga tayinlangan" (inbox) yozuvi - Qabul/Rad qilish tugmalari
// bilan. Rad etish sababi MAJBURIY (backend TaskReject validatori) -
// shuning uchun "Rad etish" tugmasi to'g'ridan-to'g'ri emas, avval kichik
// matn maydonini ochadi.
const InboxItem: React.FC<{
  task: TaskItem;
  onAccept: (id: number) => void;
  onReject: (id: number, reason: string) => Promise<void>;
}> = ({ task, onAccept, onReject }) => {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const submitReject = async () => {
    if (!reason.trim()) return;
    setSaving(true);
    try {
      await onReject(task.id, reason.trim());
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="py-2.5">
      <div className="flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: PRIORITY_DOT[task.priority] }} />
        <p className="text-sm text-gray-700 leading-snug break-words">{task.title}</p>
      </div>
      {task.assigned_by_name && (
        <p className="text-xs text-gray-400 mt-0.5">{task.assigned_by_name} tayinladi</p>
      )}
      {rejecting ? (
        <div className="mt-2 space-y-1.5">
          <input
            autoFocus
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Rad etish sababi..."
            className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-red-400"
          />
          <div className="flex gap-2">
            <button onClick={submitReject} disabled={saving || !reason.trim()}
              className="px-2.5 py-1 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 disabled:opacity-50">
              Tasdiqlash
            </button>
            <button onClick={() => setRejecting(false)} className="px-2.5 py-1 text-gray-400 text-xs hover:text-gray-600">
              Bekor
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2 mt-1.5">
          <button onClick={() => onAccept(task.id)}
            className="px-2.5 py-1 bg-green-100 text-green-700 text-xs rounded-lg hover:bg-green-200 font-medium">
            ✓ Qabul qilish
          </button>
          <button onClick={() => setRejecting(true)}
            className="px-2.5 py-1 bg-red-50 text-red-600 text-xs rounded-lg hover:bg-red-100 font-medium">
            ✕ Rad etish
          </button>
        </div>
      )}
    </div>
  );
};

const TasksWidget: React.FC<{
  tasks: TaskItem[];
  inbox: TaskItem[];
  onToggle: (id: number) => void;
  onQuickAdd: (title: string, dueDate: string) => Promise<void>;
  onAccept: (id: number) => void;
  onReject: (id: number, reason: string) => Promise<void>;
}> = ({ tasks, inbox, onToggle, onQuickAdd, onAccept, onReject }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDue, setNewDue] = useState('');
  const [saving, setSaving] = useState(false);

  const submitAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setSaving(true);
    try {
      await onQuickAdd(newTitle.trim(), newDue);
      setNewTitle('');
      setNewDue('');
      setShowAdd(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {/* Javob kutilayotgan tayinlashlar - alohida, ustunroq (harakat talab
          qiladi - oddiy checklist bilan aralashtirilmaydi). */}
      {inbox.length > 0 && (
        <div className="mb-3 pb-3 border-b border-gray-100">
          <p className="text-xs font-semibold text-orange-600 uppercase mb-1">📥 Javob kutilmoqda ({inbox.length})</p>
          <div className="divide-y divide-gray-100">
            {inbox.map(t => <InboxItem key={t.id} task={t} onAccept={onAccept} onReject={onReject} />)}
          </div>
        </div>
      )}

      {tasks.length === 0 ? (
        <p className="text-gray-400 text-sm text-center py-6">Bajarilmagan vazifalar yo'q</p>
      ) : (
        <div className="divide-y divide-gray-100">
          {tasks.map(t => {
            const due = t.due_date ? formatDueDate(t.due_date, 'uz') : null;
            return (
              <div key={t.id} className="flex items-start gap-3 py-2.5">
                <input
                  type="checkbox"
                  checked={t.done}
                  onChange={() => onToggle(t.id)}
                  className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0 cursor-pointer"
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: PRIORITY_DOT[t.priority] }} />
                    <p className="text-sm text-gray-700 leading-snug break-words">{t.title}</p>
                  </div>
                  {due && (
                    <p className={`text-xs mt-0.5 ${due.overdue ? 'text-red-500 font-medium' : due.today ? 'text-orange-500 font-medium' : 'text-gray-400'}`}>
                      {due.text}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAdd ? (
        <form onSubmit={submitAdd} className="mt-3 pt-3 border-t border-gray-100 space-y-2">
          <input
            autoFocus
            type="text"
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            placeholder="Vazifa nomi..."
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
          <div className="flex gap-2">
            <input
              type="date"
              value={newDue}
              onChange={e => setNewDue(e.target.value)}
              className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
            <button type="submit" disabled={saving || !newTitle.trim()}
              className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
              Qo'shish
            </button>
            <button type="button" onClick={() => setShowAdd(false)}
              className="px-3 py-2 text-gray-400 text-sm hover:text-gray-600">
              Bekor
            </button>
          </div>
        </form>
      ) : (
        <button onClick={() => setShowAdd(true)}
          className="mt-3 w-full text-sm text-blue-600 hover:text-blue-700 font-medium text-left">
          + Vazifa qo'shish
        </button>
      )}

      <Link to="/tasks" className="block mt-3 pt-3 border-t border-gray-100 text-sm text-blue-600 hover:text-blue-700 font-medium text-center">
        Barcha vazifalar →
      </Link>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<Stats | null>(null);
  const [monthlyOverview, setMonthlyOverview] = useState<MonthlyOverview | null>(null);
  const [topProducts, setTopProducts] = useState<TopProducts | null>(null);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [taskInbox, setTaskInbox] = useState<TaskItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/dashboard/stats'),
      api.get('/dashboard/monthly-overview'),
      api.get('/dashboard/top-products'),
      api.get('/dashboard/recent-activity'),
      // view=mine - FAQAT joriy userning o'z vazifalari (shaxsiy + qabul
      // qilingan tayinlangan) - "men tayinlaganlarim"/bo'lim nazorati bu
      // yerga ARALASHMAYDI (backend view filtri, qarang routers/tasks.py).
      api.get('/tasks', { params: { done: false, limit: 8, view: 'mine' } }),
      api.get('/tasks', { params: { view: 'inbox' } }),
    ])
      .then(([statsRes, monthlyRes, topRes, activityRes, tasksRes, inboxRes]) => {
        setStats(statsRes.data);
        setMonthlyOverview(monthlyRes.data);
        setTopProducts(topRes.data);
        setActivity(activityRes.data.items);
        setTasks(tasksRes.data);
        setTaskInbox(inboxRes.data);
      })
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  const toggleTaskDone = (id: number) => {
    const prev = tasks;
    setTasks(prev.filter(t => t.id !== id)); // optimistik: bajarilgach vidjetdan chiqadi
    api.put(`/tasks/${id}`, { done: true }).catch(e => {
      console.error(e);
      setTasks(prev); // xato bo'lsa qaytaramiz
    });
  };

  const acceptTask = (id: number) => {
    const prev = taskInbox;
    setTaskInbox(prev.filter(t => t.id !== id));
    api.put(`/tasks/${id}/accept`).then(res => {
      setTasks(t => [...t, res.data]); // qabul qilingach oddiy checklist'ga o'tadi
    }).catch(e => {
      console.error(e);
      setTaskInbox(prev); // xato bo'lsa qaytaramiz
    });
  };

  const rejectTask = async (id: number, reason: string) => {
    const prev = taskInbox;
    setTaskInbox(prev.filter(t => t.id !== id));
    try {
      await api.put(`/tasks/${id}/reject`, { reason });
    } catch (e) {
      console.error(e);
      setTaskInbox(prev);
    }
  };

  const quickAddTask = async (title: string, dueDate: string) => {
    const res = await api.post('/tasks', {
      title,
      due_date: dueDate || null,
    });
    setTasks(prev => {
      const next = [...prev, res.data];
      next.sort((a, b) => {
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return a.due_date.localeCompare(b.due_date);
      });
      return next;
    });
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-64">
      <div className="text-gray-400">Yuklanmoqda...</div>
    </div>
  );

  if (!stats) return null;

  const scopeSubtitle = stats.scope.level === 'company'
    ? "Butun kompaniya ko'rinishi"
    : stats.scope.level === 'department'
      ? `${departmentLabels[stats.scope.department || ''] || 'Bo\'lim'} ko'rinishi`
      : 'Sizga tayinlangan/yaratgan yozuvlar';

  const casesTitle = stats.scope.level === 'self' ? "Mening ochiq case'larim" : "Ochiq case'lar";
  const quotationsTitle = stats.scope.level === 'self' ? 'Mening takliflarim' : 'Tijorat takliflari';
  const purchasesTitle = stats.scope.level === 'self' ? 'Mening xarid so\'rovlarim' : 'Xarid so\'rovlari';

  const totalCasesByStatus = stats.cases?.by_status
    ? Object.values(stats.cases.by_status).reduce((a, b) => a + b, 0)
    : 0;
  const hasCaseTypeSplit = stats.cases?.sales !== undefined && stats.cases?.service !== undefined;

  const hasAnyWidget = Boolean(
    stats.products || stats.clients !== undefined || stats.suppliers !== undefined ||
    stats.cases || stats.finance || stats.contracts || stats.quotations || stats.purchases
  );

  // Widget havolalari FOYDALANUVCHI BO'LIMIGA qarab hal qilinadi - aks holda
  // (masalan doim /sales-cases'ga qattiq yozilgan bo'lsa) service_head kabi
  // boshqa bo'lim rahbari havolani bossa "Kirish huquqi yo'q" (403) ga
  // duch keladi. Xavfsizlik chegarasi baribir backendda (ModuleRoute +
  // har bir endpoint) - bu yerda faqat TO'G'RI sahifaga yo'naltiramiz.
  const dept = stats.scope.department;
  const casesPath = dept === 'service' ? '/service-cases' : '/sales-cases';
  const quotationsPath = dept === 'service' ? '/service-quotations' : '/sales-quotations';
  // "Faol/tugayotgan shartnomalar" - sales/service/accounting bo'limi va
  // kompaniya darajasi (admin/direktor, chief_accountant) /accounting'ni
  // ochа oladi (Accounting.tsx department bo'yicha tab'ni avtomatik
  // tanlaydi). FAQAT warehouse_head/logistic_head'da "accounting" moduliga
  // umuman ruxsat yo'q (DEFAULT_PERMISSIONS - permissions.py) - ularga link
  // bersak 403 beradi, shuning uchun ular uchun oddiy raqam (link emas).
  const contractsLinkable = dept !== 'warehouse';
  // Bo'lim boshlig'i uchun stats.contracts BITTA contract_type bilan
  // cheklangan (backend _contract_summary(db, "client"/"service"/"supplier")
  // - qarang dashboard.py) - shuning uchun ularning shartnoma raqami aynan
  // Accounting.tsx'dagi BITTA tab'ga mos keladi (contractTabMap bilan bir
  // xil xarita: client->supply, service->service, supplier->purchase) va
  // ?status=/?expiring= filtri bilan ANIQ mos keluvchi ro'yxatga olib borish
  // mumkin. Kompaniya/buxgalteriya uchun esa (stats.finance orqali) raqam
  // BARCHA turlarni yig'ib beradi - Accounting.tsx'da esa uchta tab ALOHIDA
  // (birlashtirilgan "hammasi" ko'rinishi yo'q), shuning uchun ULARGA faqat
  // umumiy /accounting'ga yo'naltiramiz (aniq mos keladigan bitta ro'yxat
  // yo'q - buni foydalanuvchiga aytib qo'yildi).
  const contractTabForDept: Record<string, string> = { sales: 'supply', service: 'service', warehouse: 'purchase' };
  const contractTab = dept ? contractTabForDept[dept] : undefined;
  const contractsBaseLink = contractTab ? `/accounting?tab=${contractTab}` : '/accounting';

  // Pastki status kartalari - manba turli xil (bo'lim boshlig'i uchun
  // stats.contracts, buxgalteriya/kompaniya uchun stats.finance) - shu
  // yerda bitta ro'yxatga birlashtiriladi, faqat backend haqiqatan
  // qaytargan qiymatlar ko'rsatiladi (undefined bo'lsa widget umuman
  // chizilmaydi - "bo'sh/soxta raqam ko'rsatmaslik" qoidasi).
  const activeContracts = stats.contracts?.active ?? stats.finance?.active_contracts;
  const expiringContracts = stats.contracts?.expiring ?? stats.finance?.expiring_contracts;
  type StatusCard = { key: string; label: string; value: number; icon: string; color: string; to: string | null };
  const statusCards: StatusCard[] = [];
  if (activeContracts !== undefined) {
    statusCards.push({
      key: 'active', label: 'Faol shartnomalar', value: activeContracts, icon: '📄', color: '#8B5CF6',
      to: contractsLinkable ? `${contractsBaseLink}${contractTab ? '&' : '?'}status=active` : null,
    });
  }
  if (expiringContracts !== undefined) {
    statusCards.push({
      key: 'expiring', label: '30 kun ichida tugaydigan shartnomalar', value: expiringContracts, icon: '⏰', color: '#F97316',
      to: contractsLinkable ? `${contractsBaseLink}${contractTab ? '&' : '?'}expiring=30` : null,
    });
  }
  if (stats.warranty_expiring !== undefined) {
    statusCards.push({ key: 'warranty', label: 'Kafolati tugayotgan asboblar (30 kun)', value: stats.warranty_expiring, icon: '🛡️', color: '#EAB308', to: '/service-module?warranty=expiring' });
  }
  if (stats.finance?.overdue_invoices !== undefined) {
    // Invoice'lar ham uchta turga (client/supplier/service) bo'lingan
    // ALOHIDA tab - birlashtirilgan "hammasi" ko'rinishi yo'q, shuning
    // uchun bu son (barcha turlar yig'indisi) uchun ANIQ mos keladigan
    // bitta ro'yxat yo'q - /accounting'ga yo'naltiramiz.
    statusCards.push({ key: 'overdue', label: "Muddati o'tgan hisob-fakturalar", value: stats.finance.overdue_invoices, icon: '⚠️', color: '#EF4444', to: '/accounting' });
  }

  // Oylik trend / Top mahsulotlar - ROLGA qarab ko'rsatiladi (backend
  // _quotation_scope_allowed bilan bir xil qoida: kompaniya yoki sotuv/
  // servis bo'limi), ma'lumot HALI bo'sh bo'lishi mumkinligidan qat'i
  // nazar (masalan yangi xodimda hali qabul qilingan taklif yo'q) - bo'sh
  // holat komponent ICHIDA ko'rsatiladi, butun karta yashirilmaydi.
  const quotationScopeAllowed = stats.scope.level === 'company' || dept === 'sales' || dept === 'service';

  return (
    <div className="p-3 md:p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-1 md:mb-2">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800">Dashboard</h2>
        <p className="text-xs md:text-sm text-gray-500">
          {(() => {
            const d = new Date();
            const days = ['Yak','Dush','Sesh','Chor','Pay','Jum','Shan'];
            const months = ['yan','fev','mar','apr','may','iyn','iyl','avg','sen','okt','noy','dek'];
            return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
          })()}
        </p>
      </div>
      <p className="text-xs md:text-sm text-gray-400 mb-4 md:mb-6">{scopeSubtitle}</p>

      {/* Ikki ustunli tartib (mockup'dagi kabi) - "So'nggi faoliyat" o'ng
          ustunda (desktop, lg+), telefon/planshetda asosiy tarkibdan KEYIN
          bitta ustunga tushadi (DOM tartibi - alohida media-query shart
          emas). */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
      <div className="lg:col-span-2">

      {!hasAnyWidget && (
        <div className="bg-white rounded-xl shadow p-6 text-center text-gray-400 text-sm">
          Hozircha rolingiz uchun dashboard widgetlari mavjud emas.
        </div>
      )}

      {/* Ogohlantirishlar */}
      {(
        (stats.products?.low_stock ?? 0) > 0 ||
        (stats.cases?.urgent ?? 0) > 0 ||
        (stats.finance?.unpaid_invoices ?? 0) > 0
      ) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-3 mb-4 md:mb-6">
          {(stats.products?.low_stock ?? 0) > 0 && (
            <Link to="/products" className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-3 hover:bg-red-100 transition">
              <span className="text-xl">⚠️</span>
              <div>
                <p className="text-red-700 font-medium text-sm">{stats.products!.low_stock} ta mahsulot</p>
                <p className="text-red-500 text-xs">Minimal qoldiqdan kam</p>
              </div>
            </Link>
          )}
          {(stats.cases?.urgent ?? 0) > 0 && (
            <Link to={casesPath} className="bg-orange-50 border border-orange-200 rounded-xl p-3 flex items-center gap-3 hover:bg-orange-100 transition">
              <span className="text-xl">🔥</span>
              <div>
                <p className="text-orange-700 font-medium text-sm">{stats.cases!.urgent} ta shoshilinch case</p>
                <p className="text-orange-500 text-xs">Hal qilinishini kutmoqda</p>
              </div>
            </Link>
          )}
          {(stats.finance?.unpaid_invoices ?? 0) > 0 && (
            <Link to="/accounting" className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 flex items-center gap-3 hover:bg-yellow-100 transition">
              <span className="text-xl">💳</span>
              <div>
                <p className="text-yellow-700 font-medium text-sm">{stats.finance!.unpaid_invoices} ta to'lanmagan</p>
                <p className="text-yellow-500 text-xs">Invoice kutmoqda</p>
              </div>
            </Link>
          )}
        </div>
      )}

      {/* Asosiy kartalar */}
      {(stats.products || stats.clients !== undefined || stats.suppliers !== undefined || stats.cases) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
          {stats.products && (
            <Link to="/products" className="bg-white rounded-xl shadow p-4 hover:shadow-md transition">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500 text-xs md:text-sm">Mahsulotlar</p>
                  <p className="text-2xl md:text-3xl font-bold text-gray-800 mt-1">{stats.products.total}</p>
                  {stats.products.low_stock > 0 && (
                    <p className="text-red-500 text-xs mt-1">⚠️ {stats.products.low_stock} ta kam</p>
                  )}
                </div>
                <span className="text-2xl md:text-3xl">🧪</span>
              </div>
            </Link>
          )}

          {stats.clients !== undefined && (
            <Link to="/clients" className="bg-white rounded-xl shadow p-4 hover:shadow-md transition">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500 text-xs md:text-sm">Mijozlar</p>
                  <p className="text-2xl md:text-3xl font-bold text-gray-800 mt-1">{stats.clients}</p>
                  <p className="text-gray-400 text-xs mt-1">Faol mijozlar</p>
                </div>
                <span className="text-2xl md:text-3xl">👔</span>
              </div>
            </Link>
          )}

          {stats.suppliers !== undefined && (
            <Link to="/suppliers" className="bg-white rounded-xl shadow p-4 hover:shadow-md transition">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500 text-xs md:text-sm">Yetkazib beruvchilar</p>
                  <p className="text-2xl md:text-3xl font-bold text-gray-800 mt-1">{stats.suppliers}</p>
                  <p className="text-gray-400 text-xs mt-1">Faol</p>
                </div>
                <span className="text-2xl md:text-3xl">🏭</span>
              </div>
            </Link>
          )}

          {stats.cases && (
            <Link to={casesPath} className="bg-white rounded-xl shadow p-4 hover:shadow-md transition">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500 text-xs md:text-sm">{casesTitle}</p>
                  <p className="text-2xl md:text-3xl font-bold text-gray-800 mt-1">{stats.cases.open}</p>
                  {stats.cases.urgent > 0 && (
                    <p className="text-red-500 text-xs mt-1">🔥 {stats.cases.urgent} ta shoshilinch</p>
                  )}
                </div>
                <span className="text-2xl md:text-3xl">🎯</span>
              </div>
            </Link>
          )}
        </div>
      )}

      {/* Moliya kartalar (pul ko'rsatkichlari - shartnoma/invoice holati
          pastdagi umumiy "status kartalari" qatorida, takrorlanmasin) */}
      {stats.finance && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-4 md:mb-6">
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow p-4 text-white">
            <p className="text-green-100 text-xs md:text-sm">Kirim (USD)</p>
            <p className="text-xl md:text-2xl font-bold mt-1">${stats.finance.income_usd.toLocaleString()}</p>
          </div>
          <div className="bg-gradient-to-br from-green-400 to-green-500 rounded-xl shadow p-4 text-white">
            <p className="text-green-100 text-xs md:text-sm">Kirim (UZS)</p>
            <p className="text-xl md:text-2xl font-bold mt-1">{stats.finance.income_uzs.toLocaleString()}</p>
            <p className="text-green-100 text-xs">so'm</p>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow p-4 text-white">
            <p className="text-blue-100 text-xs md:text-sm">Foyda (USD)</p>
            <p className="text-xl md:text-2xl font-bold mt-1">${stats.finance.profit_usd.toLocaleString()}</p>
          </div>
        </div>
      )}

      {/* Status kartalari - Faol/Tugayotgan shartnomalar, Kafolat tugaydi,
          Muddati o'tgan invoicelar. Manbasidan qat'i nazar (bo'lim boshlig'i
          -> stats.contracts, buxgalteriya/kompaniya -> stats.finance) BIR
          XIL qatorda - faqat backend HAQIQATAN qaytargan metrikalar
          ko'rsatiladi. Havolalar avvalgi bosqichda tuzatilgan qoidani
          davom ettiradi: rol ochа olmaydigan sahifaga link berilmaydi. */}
      {statusCards.length > 0 && (
        <div className="mb-4 md:mb-6">
          <h3 className="font-semibold text-gray-700 text-sm md:text-base mb-2 md:mb-3">📌 Muddatlar va holat</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {statusCards.map(card => {
            const body = (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-xl">{card.icon}</span>
                </div>
                <p className="text-xl md:text-2xl font-bold mt-2" style={{ color: card.color }}>{card.value}</p>
                <p className="text-gray-500 text-xs mt-1">{card.label}</p>
              </>
            );
            return card.to ? (
              <Link key={card.key} to={card.to} className="bg-white rounded-xl shadow p-4 hover:shadow-md transition" style={{ borderTop: `3px solid ${card.color}` }}>
                {body}
              </Link>
            ) : (
              <div key={card.key} className="bg-white rounded-xl shadow p-4" style={{ borderTop: `3px solid ${card.color}` }}>
                {body}
              </div>
            );
          })}
          </div>
        </div>
      )}

      {/* Ombor holati (donut) - director/admin va warehouse_head/logistic_head
          UCHUN (backend inventory_summary shu rollarga qaytariladi - sales/
          service bo'limiga ombor ma'lumoti berilmaydi). */}
      {stats.inventory_summary && (
        <div className="bg-white rounded-xl shadow p-4 md:p-5 mb-4 md:mb-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-gray-700 text-sm md:text-base">📦 Ombor holati</h3>
            <Link to="/products" className="text-blue-500 text-xs hover:underline">Mahsulotlar →</Link>
          </div>
          <InventoryDonut data={stats.inventory_summary} />
        </div>
      )}

      {/* Grafiklar */}
      {(quotationScopeAllowed || stats.cases?.by_status) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 md:mb-6">
          {quotationScopeAllowed && monthlyOverview && (
            <div className="md:col-span-2 bg-white rounded-xl shadow p-4 md:p-5">
              <h3 className="font-semibold text-gray-700 mb-4 text-sm md:text-base">
                {dept === 'service' ? 'Oylik xizmat takliflari' : 'Oylik sotuv trendi'}
              </h3>
              {monthlyOverview.months.some(m => m.count > 0) ? (
                <MonthlyTrendChart months={monthlyOverview.months} />
              ) : (
                <p className="text-gray-400 text-sm text-center py-6">Hali ma'lumot yo'q</p>
              )}
            </div>
          )}

          {stats.cases?.by_status && (
            <div className="bg-white rounded-xl shadow p-4 md:p-5">
              <h3 className="font-semibold text-gray-700 mb-4 text-sm md:text-base">Case'lar holati</h3>
              <div className="space-y-2">
                {Object.entries(stats.cases.by_status).map(([status, count]) => {
                  const pct = totalCasesByStatus > 0 ? (count / totalCasesByStatus) * 100 : 0;
                  const cfg = caseStatusLabels[status];
                  return (
                    <div key={status}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-600">{cfg?.label}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div className="h-2 rounded-full transition-all"
                          style={{ width: `${pct}%`, backgroundColor: cfg?.color }} />
                      </div>
                    </div>
                  );
                })}
                {totalCasesByStatus === 0 && <p className="text-gray-400 text-sm text-center py-4">Case'lar yo'q</p>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pastki kartalar */}
      {(stats.quotations || stats.purchases || hasCaseTypeSplit || quotationScopeAllowed) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {stats.quotations && (
            <div className="bg-white rounded-xl shadow p-4 md:p-5">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-gray-700 text-sm md:text-base">{quotationsTitle}</h3>
                <Link to={quotationsPath} className="text-blue-500 text-xs hover:underline">Barchasi →</Link>
              </div>
              <div className="space-y-2 md:space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">Jami</span>
                  <span className="font-bold text-gray-800">{stats.quotations.total}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">Kutilmoqda</span>
                  <span className="font-bold text-yellow-600">{stats.quotations.pending}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">Qabul qilindi</span>
                  <span className="font-bold text-green-600">{stats.quotations.accepted}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2 mt-2">
                  <div className="h-2 bg-green-500 rounded-full"
                    style={{ width: `${stats.quotations.total > 0 ? (stats.quotations.accepted / stats.quotations.total) * 100 : 0}%` }} />
                </div>
                <p className="text-xs text-gray-400">
                  {stats.quotations.total > 0
                    ? `${Math.round((stats.quotations.accepted / stats.quotations.total) * 100)}% qabul qilindi`
                    : "Takliflar yo'q"}
                </p>
                {stats.quotations.sales !== undefined && stats.quotations.service !== undefined && (
                  <div className="pt-2 mt-1 border-t space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                        <span className="text-gray-500 text-sm">Sotuv</span>
                      </div>
                      <span className="font-bold text-gray-800">{stats.quotations.sales}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 bg-purple-500 rounded-full"></span>
                        <span className="text-gray-500 text-sm">Xizmat</span>
                      </div>
                      <span className="font-bold text-gray-800">{stats.quotations.service}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {stats.purchases && (
            <div className="bg-white rounded-xl shadow p-4 md:p-5">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-gray-700 text-sm md:text-base">{purchasesTitle}</h3>
                <Link to="/purchases" className="text-blue-500 text-xs hover:underline">Barchasi →</Link>
              </div>
              <div className="space-y-2 md:space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">Jami</span>
                  <span className="font-bold text-gray-800">{stats.purchases.total}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">Kutilmoqda</span>
                  <span className="font-bold text-yellow-600">{stats.purchases.pending}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-500 text-sm">Bajarildi</span>
                  <span className="font-bold text-green-600">{stats.purchases.total - stats.purchases.pending}</span>
                </div>
              </div>
            </div>
          )}

          {hasCaseTypeSplit && (
            <div className="bg-white rounded-xl shadow p-4 md:p-5">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-gray-700 text-sm md:text-base">Case'lar turlari</h3>
                <Link to={casesPath} className="text-blue-500 text-xs hover:underline">Barchasi →</Link>
              </div>
              <div className="space-y-2 md:space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                    <span className="text-gray-500 text-sm">Sales</span>
                  </div>
                  <span className="font-bold text-gray-800">{stats.cases!.sales}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 bg-purple-500 rounded-full"></span>
                    <span className="text-gray-500 text-sm">Service</span>
                  </div>
                  <span className="font-bold text-gray-800">{stats.cases!.service}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3 mt-2 overflow-hidden">
                  {(stats.cases!.sales! + stats.cases!.service!) > 0 && (
                    <div className="h-3 flex">
                      <div className="bg-blue-500 transition-all"
                        style={{ width: `${(stats.cases!.sales! / (stats.cases!.sales! + stats.cases!.service!)) * 100}%` }} />
                      <div className="bg-purple-500 transition-all flex-1" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {quotationScopeAllowed && topProducts && (
            <div className="bg-white rounded-xl shadow p-4 md:p-5">
              <h3 className="font-semibold text-gray-700 text-sm md:text-base mb-3">
                {dept === 'service' ? 'Eng ko\'p so\'ralgan xizmatlar' : 'Eng ko\'p sotilgan mahsulotlar'}
              </h3>
              <TopProductsList products={topProducts.products} />
            </div>
          )}
        </div>
      )}

      </div>

      {/* So'nggi faoliyat - lg:sticky bilan uzun asosiy tarkib scroll
          qilinganda ham ko'rinib turadi; max-h+overflow ro'yxat cheksiz
          o'sib ketmasligi uchun (backend limit=12 qaytaradi, baribir). */}
      <div className="lg:col-span-1 space-y-4 md:space-y-6">
        {/* Vazifalar va eslatmalar - shaxsiy (Model P), har bir rol uchun
            bir xil ko'rinadi (bo'lim/rol scope'iga bog'liq emas). */}
        <div className="bg-white rounded-xl shadow p-4 md:p-5">
          <h3 className="font-semibold text-gray-700 mb-1 text-sm md:text-base">✅ Vazifalar va eslatmalar</h3>
          <TasksWidget tasks={tasks} inbox={taskInbox} onToggle={toggleTaskDone} onQuickAdd={quickAddTask} onAccept={acceptTask} onReject={rejectTask} />
        </div>

        <div className="bg-white rounded-xl shadow p-4 md:p-5 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
          <h3 className="font-semibold text-gray-700 mb-1 text-sm md:text-base">🕐 So'nggi faoliyat</h3>
          <RecentActivity items={activity} />
        </div>
      </div>

      </div>
    </div>
  );
};

export default Dashboard;
